import type { ProcedureItem } from 'features/Procedure/state/procedure-api.type'
import {
  DrawingElementVisibleState,
  ProcedureTypeCode,
  type DrawingElement,
} from 'features/Procedure/state/procedure.type'

type ExistingProcedure = {
  cpt4: ProcedureItem
  icd10s: ProcedureItem[]
}

export const formatProcedureForEdit = (procedure: ProcedureItem): ExistingProcedure | null => {
  if (procedure.code_type !== ProcedureTypeCode.CPT4) {
    return null
  }

  return {
    cpt4: procedure,
    icd10s: [],
  }
}

const parseProcedureDrawingElements = (
  procedure: ProcedureItem | null | undefined,
): DrawingElement[] => {
  const drawings = procedure?.drawings?.drawingElements

  if (!drawings) {
    return []
  }

  if (Array.isArray(drawings)) {
    return drawings
  }

  if (typeof drawings !== 'string') {
    return []
  }

  try {
    const parsedDrawings = JSON.parse(drawings)
    return Array.isArray(parsedDrawings) ? parsedDrawings : []
  } catch {
    return []
  }
}

const hasNonEmptyText = (value?: string | null): boolean => {
  return typeof value === 'string' && value.trim().length > 0
}

export const hasProcedureEnteredData = (
  procedure: ProcedureItem | null | undefined,
  drawingElements: DrawingElement[] = [],
): boolean => {
  if (!procedure?.id) {
    return false
  }

  const hasPersistedDrawings = parseProcedureDrawingElements(procedure).length > 0

  const hasLocalVisibleDrawings = drawingElements.some(
    (drawingElement) =>
      drawingElement.procedureId === procedure.id &&
      (drawingElement.visibleState === DrawingElementVisibleState.VISIBLE ||
        drawingElement.visibleState === DrawingElementVisibleState.PERSISTED),
  )

  const hasProcedureNote = hasNonEmptyText(procedure.note)

  const hasProductLabels = Array.isArray(procedure.product_labels)
    ? procedure.product_labels.some((label) => hasNonEmptyText(label))
    : false

  const hasOptionalProcedureDetails =
    hasNonEmptyText(procedure.area) ||
    hasNonEmptyText(procedure.energy) ||
    hasNonEmptyText(procedure.pulse_per_section) ||
    hasNonEmptyText(procedure.total_energy)

  const hasAttachedDocument = Boolean(
    procedure.doc_template?.id ||
      procedure.doc_template?.document_id ||
      procedure.doc_template?.has_doc_to_sign,
  )

  return (
    hasPersistedDrawings ||
    hasLocalVisibleDrawings ||
    hasProcedureNote ||
    hasProductLabels ||
    hasOptionalProcedureDetails ||
    hasAttachedDocument
  )
}
